import React from "react";
import EntitySelectField, { type EntitySelectFieldProps } from "./EntitySelectField";
import ContentStringField from "./ContentStringField";
import ArrayTextField from "./ArrayTextField";
import ContentArrayContentField from "./ContentArrayContentField";
import FaqField from "./FaqField";
import InfotextField from "./InfotextField";
import BottomBlockLeftField from "./BottomBlockLeftField";
import BottomBlockRightField from "./BottomBlockRightField";
import CategoryMenuField from "./CategoryMenuField";
import SitemapField from "./SitemapField";
import ContentServiceField from "./ContentServiceField";
import RichTextField from "./RichTextField";
import LinkField from "./LinkField";
import { bindFieldProps } from "./VariableField";

/**
 * CONTENT_OVERRIDES — the content-mode delta on the VariableField registry
 * (SCRUM-641, Pages step 2 of SCRUM-639). The content hosts (wizard + both
 * update screens) pass this via VariableField's `overrides` prop when they
 * get wired in the later steps; per-host props (e.g. `dateDisabled` for the
 * update-Required midnightExpress quirk) are bound with a closure entry.
 *
 * - `String`: ContentStringField (authorname/date name special-cases).
 * - Entity types `Product` / `Post`: single-select mode — content variables
 *   store ONE full doc where the settings side stores arrays. `Post` is
 *   deliberately NOT in the shared REGISTRY: a settings variable of type Post
 *   renders nothing today and must keep doing so.
 *
 * SCRUM-2207 removed Category / Subcategory / Ad / Blog, `tips`, and the
 * `Json` / `Image` null pins (SCRUM-648 D2): no template declares any of them,
 * and with Json / Image gone from the shared REGISTRY the dispatcher returns
 * null for them anyway, so a host's "Unknown type" fallback still applies.
 */
const EntitySingleSelectField = (props: EntitySelectFieldProps) => (
  <EntitySelectField {...props} isMulti={false} />
);

export const CONTENT_OVERRIDES = {
  String: ContentStringField,
  // The content screens hint the URL format in the link half (SCRUM-2197).
  // The settings side keeps the shared registry's plain "link" placeholder.
  Link: bindFieldProps(LinkField, { linkPlaceholder: "link (https://...)" }),
  // SCRUM-929 — first-class rich-text content type: a Quill WYSIWYG whose value
  // is an HTML string (rendered as HTML, e.g. by devlink/BottomText.js). Reaches
  // the Settings screen (HandleContent (both statuses)) AND the create wizard
  // (its `shared` map spreads CONTENT_OVERRIDES); the Edit screen keys off the
  // page_content entry type separately (UpdatePageContent.jsx).
  html: RichTextField,
  Product: EntitySingleSelectField,
  Post: EntitySingleSelectField,
  // Composite content types (SCRUM-642) — pages-only, so like Post they stay
  // OUT of the shared REGISTRY (a settings variable of these types renders
  // nothing today and must keep doing so). Hosts bind per-surface props
  // (variant, summaryProps, pages/categories, validation) via closures at
  // wiring time (SCRUM-643); the bare entries are the update-flavoured
  // defaults. `service` IS in the shared REGISTRY (settings ServiceField) and
  // must be overridden on the content side with the pages variant.
  faq: FaqField,
  infotext: InfotextField,
  bottomblockleft: BottomBlockLeftField,
  bottomblockright: BottomBlockRightField,
  categorymenu: CategoryMenuField,
  sitemap: SitemapField,
  service: ContentServiceField,
  // Array routing on the content side (SCRUM-643). The dispatcher checks the
  // specific `Array:<complextype>` key before the plain `Array` key. The
  // multi entity complextypes share EntitySelectField (default isMulti);
  // hosts inject the matching options list and keep the per-type
  // post-processing (sponsored-first sort, the Optional/wizard global-tag
  // inject) in their onChange adapter so the blocks stay pure.
  // Array:text = the wizard's shared block (SCRUM-648 D1); the UPDATE hosts
  // override it with ContentArrayTextField (their pinned icon-button editor).
  "Array:text": ArrayTextField,
  // Array:content = the content editor (ellipsis placeholders, per-surface
  // chrome via variant) — NOT the settings-side ArrayContentField.
  "Array:content": ContentArrayContentField,
  "Array:product": EntitySelectField,
  "Array:category": EntitySelectField,
  "Array:subcategory": EntitySelectField,
  "Array:posts": EntitySelectField,
  // `Array:links` is deliberately absent: the two legacy editors differ per
  // surface (wizard ArrayLinks vs update ContentArrayLinks), so each host
  // binds its own entry. Every OTHER complextype renders nothing — the
  // content side has no `;`-grammar editor, so the shared ARRAY_REGISTRY
  // fallback (ArraySimpleField) must never leak in here; the host's legacy
  // "Unknown type" fallback stays in charge (SCRUM-648-style pinned absence).
  Array: null,
};
